// ============================================================================
// SECOND SCREEN CONFIG PANEL - Sidebar view for second screen management
// Controls opening, presets, aspect ratio, and layout customization
// Supports independent configuration for second screen and third screen
// ============================================================================

import React, { useState, useRef, useEffect } from 'react';
import {
  Monitor,
  LayoutGrid,
  User,
  Presentation,
  BarChart3,
  Shield,
  Sparkles,
  Network,
  FileOutput,
  Info,
  Grid,
  ScanLine,
  RotateCcw,
  MessageCircle,
  Shuffle,
} from 'lucide-react';
import { PRESET_LAYOUTS } from '../../second-screen/presets';
import { recordTrainingEvent } from '../../../services/trainingAssessment';
import SecondScreenLayoutEditor from './SecondScreenLayoutEditor';
import type { SecondScreenLayout, OverscanSettings, SessionAvatar } from '../../../types/second-screen.types';

const LAYOUT_KEY_PREFIX = 'second-screen-layout-';
const OVERSCAN_KEY_PREFIX = 'second-screen-overscan-';
const DEFAULT_OVERSCAN: OverscanSettings = { top: 0, bottom: 0, left: 0, right: 0 };

function storageKey(prefix: string, sessionId: string, screenNumber: number): string {
  return `${prefix}${sessionId}-screen${screenNumber}`;
}

// Map preset IDs to lucide icons
const PRESET_ICONS: Record<string, React.ReactNode> = {
  'mission-control': <LayoutGrid className="w-5 h-5" />,
  'avatar-focus': <User className="w-5 h-5" />,
  'presentation': <Presentation className="w-5 h-5" />,
  'data-deep-dive': <BarChart3 className="w-5 h-5" />,
  'facilitator': <Shield className="w-5 h-5" />,
  'inspiration-wall': <Sparkles className="w-5 h-5" />,
  'knowledge-graph': <Network className="w-5 h-5" />,
  'task-outputs': <FileOutput className="w-5 h-5" />,
};

interface SecondScreenConfigPanelProps {
  sessionId: string;
  /** Only needed to record training assessment evidence. */
  projectId?: string;
  isOpen: boolean;
  onOpen: () => void;
  isThirdScreenOpen?: boolean;
  onOpenThirdScreen?: () => void;
  onActiveScreenChange?: (screen: number | undefined) => void;
  sessionAvatars?: SessionAvatar[];
  onTriggerBubble?: (avatar?: SessionAvatar) => void;
}

const SecondScreenConfigPanel: React.FC<SecondScreenConfigPanelProps> = ({
  sessionId,
  projectId,
  isOpen,
  onOpen,
  isThirdScreenOpen,
  onOpenThirdScreen,
  onActiveScreenChange,
  sessionAvatars = [],
  onTriggerBubble,
}) => {
  const [showEditor, setShowEditor] = useState(false);
  const [activeScreen, setActiveScreenLocal] = useState<1 | 2>(1);
  const [overscan, setOverscanLocal] = useState<OverscanSettings>(DEFAULT_OVERSCAN);
  const channelRef = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    channelRef.current = new BroadcastChannel(`spectrum-second-screen-${sessionId}`);
    return () => { channelRef.current?.close(); channelRef.current = null; };
  }, [sessionId]);

  // Reload overscan from localStorage when the active screen changes
  useEffect(() => {
    try {
      const raw = localStorage.getItem(`${OVERSCAN_KEY_PREFIX}${sessionId}-screen${activeScreen}`);
      setOverscanLocal(raw ? JSON.parse(raw) : DEFAULT_OVERSCAN);
    } catch { setOverscanLocal(DEFAULT_OVERSCAN); }
  }, [sessionId, activeScreen]);

  const applyOverscan = (next: OverscanSettings) => {
    setOverscanLocal(next);
    localStorage.setItem(`${OVERSCAN_KEY_PREFIX}${sessionId}-screen${activeScreen}`, JSON.stringify(next));
    channelRef.current?.postMessage({
      type: 'overscan',
      sessionId,
      seq: Date.now(),
      ts: Date.now(),
      targetScreen: activeScreen,
      settings: next,
    });
  };

  const setOverscanField = (field: keyof OverscanSettings, value: number) => {
    applyOverscan({ ...overscan, [field]: value });
  };

  const setActiveScreen = (screen: 1 | 2) => {
    setActiveScreenLocal(screen);
    // Notify parent of the target screen number for pin/spotlight isolation
    // Screen 1 in the config panel = screen number 1 (second screen), Screen 2 = screen number 2 (third screen)
    onActiveScreenChange?.(screen);
  };

  // Reset to screen 1 when third screen is no longer available
  React.useEffect(() => {
    if (!onOpenThirdScreen && activeScreen === 2) {
      setActiveScreen(1);
    }
  }, [onOpenThirdScreen, activeScreen]);

  const getCurrentLayout = (): SecondScreenLayout => {
    try {
      const raw = localStorage.getItem(storageKey(LAYOUT_KEY_PREFIX, sessionId, activeScreen));
      return raw ? JSON.parse(raw) : PRESET_LAYOUTS[0];
    } catch { return PRESET_LAYOUTS[0]; }
  };

  const getActivePresetId = (): string | null => {
    try {
      const raw = localStorage.getItem(storageKey(LAYOUT_KEY_PREFIX, sessionId, activeScreen));
      if (raw) {
        const layout = JSON.parse(raw);
        return layout.id || null;
      }
      return PRESET_LAYOUTS[0]?.id || null;
    } catch { return null; }
  };

  const applyPreset = (presetIndex: number) => {
    const preset = PRESET_LAYOUTS[presetIndex];
    if (preset) {
      localStorage.setItem(storageKey(LAYOUT_KEY_PREFIX, sessionId, activeScreen), JSON.stringify(preset));
      // Layouts never reach the server, so the training assessment has no way
      // to observe this without an explicit event. Fire and forget.
      if (projectId) {
        recordTrainingEvent(projectId, { type: 'secondScreenConfigured' }).catch(() => {});
      }
    }
  };

  const activePresetId = getActivePresetId();

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-xl font-extrabold text-slate-900 tracking-tight">Screens</h2>
          <p className="text-sm text-slate-500 mt-1 leading-relaxed">
            Configure mission control displays for your room's large screens
          </p>
        </div>

        {/* Active Screens */}
        <section>
          <h3 className="text-[0.65rem] uppercase tracking-widest font-bold text-slate-400 px-1 mb-3">Active Screens</h3>
          <div className="space-y-3">
            {/* Screen 1 */}
            <div className="bg-slate-50 rounded-2xl p-4 flex items-center justify-between transition-all duration-200 hover:bg-white hover:shadow-lg hover:shadow-slate-900/5">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className={`w-2.5 h-2.5 rounded-full ${isOpen ? 'bg-primary' : 'bg-slate-300'}`} />
                  {isOpen && (
                    <div className="absolute inset-0 w-2.5 h-2.5 rounded-full bg-primary animate-ping opacity-75" />
                  )}
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900">Screen 1</p>
                  <p className="text-[11px] text-slate-400">Second screen</p>
                </div>
              </div>
              <button
                data-training-id="open-second-screen-button"
                onClick={onOpen}
                className={`text-xs font-bold px-4 py-1.5 rounded-xl transition-all ${
                  isOpen
                    ? 'bg-primary/10 text-primary hover:bg-primary hover:text-white'
                    : 'bg-white border border-slate-200 text-slate-500 hover:border-primary hover:text-primary'
                }`}
              >
                {isOpen ? 'Focus' : 'Open'}
              </button>
            </div>

            {/* Screen 2 (Third Screen) */}
            {onOpenThirdScreen && (
              <div className="bg-slate-50 rounded-2xl p-4 flex items-center justify-between transition-all duration-200 hover:bg-white hover:shadow-lg hover:shadow-slate-900/5">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className={`w-2.5 h-2.5 rounded-full ${isThirdScreenOpen ? 'bg-primary' : 'bg-slate-300'}`} />
                    {isThirdScreenOpen && (
                      <div className="absolute inset-0 w-2.5 h-2.5 rounded-full bg-primary animate-ping opacity-75" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900">Screen 2</p>
                    <p className="text-[11px] text-slate-400">Third screen</p>
                  </div>
                </div>
                <button
                  onClick={onOpenThirdScreen}
                  className={`text-xs font-bold px-4 py-1.5 rounded-xl transition-all ${
                    isThirdScreenOpen
                      ? 'bg-primary/10 text-primary hover:bg-primary hover:text-white'
                      : 'bg-white border border-slate-200 text-slate-500 hover:border-primary hover:text-primary'
                  }`}
                >
                  {isThirdScreenOpen ? 'Focus' : 'Open'}
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Control Target */}
        {onOpenThirdScreen && (
          <section>
            <label className="text-[0.65rem] uppercase tracking-widest font-bold text-slate-400 px-1 block mb-2">Control Target</label>
            <div className="bg-slate-100 p-1 rounded-2xl flex h-10">
              <button
                onClick={() => setActiveScreen(1)}
                className={`flex-1 text-xs font-semibold rounded-xl transition-all ${
                  activeScreen === 1
                    ? 'bg-white text-slate-900 shadow-md'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Screen 2
              </button>
              <button
                onClick={() => setActiveScreen(2)}
                className={`flex-1 text-xs font-semibold rounded-xl transition-all ${
                  activeScreen === 2
                    ? 'bg-white text-slate-900 shadow-md'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Screen 3
              </button>
            </div>
          </section>
        )}

        {/* Layout Presets */}
        <section>
          <div className="flex items-center justify-between px-1 mb-3">
            <h3 className="text-[0.65rem] uppercase tracking-widest font-bold text-slate-400">Layout Presets</h3>
            {activePresetId && (
              <span className="text-[9px] bg-primary/10 text-primary font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                Active
              </span>
            )}
          </div>
          <div className="grid grid-cols-5 gap-2">
            {PRESET_LAYOUTS.map((preset, index) => {
              const isActive = activePresetId === preset.id;
              return (
                <button
                  key={preset.id}
                  onClick={() => applyPreset(index)}
                  className={`rounded-xl p-2.5 flex flex-col items-center justify-center text-center gap-1.5 transition-all cursor-pointer ${
                    isActive
                      ? 'bg-primary/5 border-2 border-primary shadow-lg shadow-primary/10'
                      : 'bg-slate-50 border border-transparent hover:bg-white hover:ring-2 hover:ring-primary/20 hover:shadow-lg hover:shadow-slate-900/5'
                  }`}
                >
                  <div className={`p-1.5 rounded-lg ${
                    isActive
                      ? 'bg-primary text-white'
                      : 'bg-white shadow-sm text-primary group-hover:bg-primary/5'
                  }`}>
                    {PRESET_ICONS[preset.id] || <Monitor className="w-4 h-4" />}
                  </div>
                  <div>
                    <p className={`text-[10px] font-bold leading-tight ${isActive ? 'text-primary' : 'text-slate-800'}`}>
                      {preset.name}
                    </p>
                    <p className={`text-[9px] mt-0.5 ${isActive ? 'text-primary/70' : 'text-slate-400'}`}>
                      {preset.widgets.length} widgets
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* Avatar Mind Bubbles — force trigger on demand */}
        {onTriggerBubble && (
          <section>
            <h3 className="text-[0.65rem] uppercase tracking-widest font-bold text-slate-400 px-1 mb-3">Avatar Mind Bubbles</h3>
            <div className="bg-slate-50 rounded-2xl p-4 space-y-3">
              <button
                onClick={() => onTriggerBubble()}
                disabled={sessionAvatars.length === 0}
                className="w-full flex items-center justify-center gap-2 text-xs font-bold px-4 py-2 rounded-xl bg-primary/10 text-primary hover:bg-primary hover:text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Shuffle className="w-3.5 h-3.5" />
                Force bubble now (random avatar)
              </button>
              {sessionAvatars.length > 0 && (
                <div className="space-y-1.5">
                  {sessionAvatars.map(avatar => (
                    <button
                      key={avatar.role}
                      onClick={() => onTriggerBubble(avatar)}
                      className="w-full flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-xl bg-white border border-slate-200 text-slate-600 hover:border-primary hover:text-primary transition-colors"
                    >
                      <MessageCircle className="w-3.5 h-3.5 shrink-0" />
                      {avatar.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}

        {/* Customize Layout Button */}
        <button
          onClick={() => setShowEditor(true)}
          className="w-full bg-primary hover:brightness-95 text-white font-bold py-3 rounded-2xl flex items-center justify-center gap-2 transition-all shadow-lg shadow-primary/20 active:scale-[0.98]"
        >
          <Grid className="w-4 h-4" />
          Customize Layout
        </button>

        {/* Overscan / Safe Area */}
        <section>
          <div className="flex items-center gap-2 px-1 mb-3">
            <ScanLine className="w-3.5 h-3.5 text-slate-400" />
            <h3 className="text-[0.65rem] uppercase tracking-widest font-bold text-slate-400">Overscan / Safe Area</h3>
          </div>
          <div className="bg-slate-50 rounded-2xl p-4 space-y-3">
            <p className="text-[10px] text-slate-400 leading-relaxed">
              Inset content to correct for TV overscan or projector clipping. Values in px.
            </p>
            {(['top', 'bottom', 'left', 'right'] as const).map(side => (
              <div key={side} className="flex items-center justify-between gap-3">
                <label className="text-xs font-semibold text-slate-600 capitalize w-12">{side}</label>
                <div className="flex items-center gap-1.5 flex-1">
                  <button
                    onClick={() => setOverscanField(side, overscan[side] - 1)}
                    className="w-7 h-7 rounded-lg bg-white border border-slate-200 text-slate-500 text-sm font-bold hover:border-primary hover:text-primary transition-colors flex items-center justify-center"
                  >−</button>
                  <input
                    type="number"
                    value={overscan[side]}
                    onChange={e => setOverscanField(side, parseInt(e.target.value) || 0)}
                    className="flex-1 text-center text-sm font-bold text-slate-900 bg-white border border-slate-200 rounded-lg h-7 focus:outline-none focus:border-primary"
                  />
                  <button
                    onClick={() => setOverscanField(side, overscan[side] + 1)}
                    className="w-7 h-7 rounded-lg bg-white border border-slate-200 text-slate-500 text-sm font-bold hover:border-primary hover:text-primary transition-colors flex items-center justify-center"
                  >+</button>
                </div>
              </div>
            ))}
            <button
              onClick={() => applyOverscan(DEFAULT_OVERSCAN)}
              className="w-full flex items-center justify-center gap-1.5 text-[10px] font-bold text-slate-400 hover:text-slate-600 transition-colors pt-1"
            >
              <RotateCcw className="w-3 h-3" />
              Reset to zero
            </button>
          </div>
        </section>

        {/* Expert Tip */}
        <div className="bg-white rounded-2xl p-4 border border-slate-200/60 shadow-sm">
          <div className="flex gap-2.5 mb-1.5">
            <Info className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            <p className="text-[10px] font-bold text-slate-700 uppercase tracking-wider">Expert Tip</p>
          </div>
          <p className="text-[11px] text-slate-500 leading-relaxed pl-6">
            Drag screens to external displays and press <span className="font-bold text-slate-700">F11</span> for fullscreen. Use the floating toolbar to switch presets.
          </p>
        </div>
      </div>

      {/* Layout Editor Modal */}
      {showEditor && (
        <SecondScreenLayoutEditor
          sessionId={sessionId}
          screenNumber={activeScreen}
          initialLayout={getCurrentLayout()}
          onClose={() => setShowEditor(false)}
        />
      )}
    </div>
  );
};

export default SecondScreenConfigPanel;
